import { Component } from '@theme/component';

/**
 * @typedef {Object} CollectionTabsRefs
 * @property {HTMLElement[]} tabs
 */

/** @extends {Component<CollectionTabsRefs>} */
export class CollectionTabsComponent extends Component {
  requiredRefs = ['tabs'];
  #active = 0;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {HTMLElement | null} */
  #panelsContainer = null;
  /** @type {IntersectionObserver | null} */
  #panelObserver = null;
  /** タブ操作起点のスムーズスクロール中はスワイプ同期を抑止するための目的インデックス */
  /** @type {number | null} */
  #scrollTarget = null;
  #mobileQuery = window.matchMedia('(max-width: 989px)');
  /** ループ用: 先頭パネルのクローン（末尾に置き、到達時に実先頭へワープ） */
  /** @type {HTMLElement | null} */
  #loopClone = null;
  /** ループ用: 最終パネルのクローン（先頭に置き、到達時に実最終へワープ） */
  /** @type {HTMLElement | null} */
  #loopHeadClone = null;
  /** @type {AbortController | null} */
  #loopAbort = null;
  /** 表示中パネルにコンテナ高さを追従させる（横並び時の「最も高いパネル」由来の余白対策） */
  /** @type {ResizeObserver | null} */
  #heightObserver = null;
  /** 遅延パネルを表示前に展開するための監視（SPのスワイプ用） */
  /** @type {IntersectionObserver | null} */
  #lazyObserver = null;

  /**
   * 初回操作（タッチ/スクロール/タブ操作）までスワイプ系の本組みを遅延するフラグ。
   * 初期表示はアクティブタブの10枚だけをDOMに載せ、ループ用クローン(パネル2枚分)・
   * 先頭/末尾パネルの展開・隣接先読み(IntersectionObserver)は触れた瞬間に行う。
   * トップページの初期DOMを約2,000ノード削減するための遅延で、組み上がった後の
   * 挙動（ループ・スワイプ同期・高さ追従）は従来と同一。
   */
  #swipeReady = false;

  connectedCallback() {
    super.connectedCallback();
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    this.#panelsContainer = this.querySelector('.collection-tabs__panels');

    this.#active = Math.max(
      0,
      tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')
    );

    this.#setupEventListeners();
    this.#hydratePanel(this.#active);
    this.#updateActiveTab();
    this.#setupSwipeSync();
  }

  /**
   * 非アクティブなタブの中身は section 側で <template data-lazy-panel-content> に入っている。
   * DOM にもレイアウトにも乗らないので、開くまでノード数・画像読み込み・スタイル再計算が発生しない。
   * 表示する直前にここで中身をパネルへ移す。カード用の各スクリプト
   * （product-ranking / product-card-row-align / swym-card-heart-position / Swym本体）は
   * いずれも body の childList を監視しているので、移した時点で自動的に走る。
   * @param {number | HTMLElement} target パネルの index または要素
   */
  #hydratePanel(target) {
    const panel =
      typeof target === 'number'
        ? /** @type {HTMLElement | undefined} */ (
            this.querySelectorAll('.collection-tabs__panel:not([data-loop-clone])')[target]
          )
        : target;
    if (!panel || panel.dataset.lazyPanel !== '1') return false;

    const template = /** @type {HTMLTemplateElement | null} */ (
      panel.querySelector('template[data-lazy-panel-content]')
    );
    delete panel.dataset.lazyPanel;
    if (!template) return false;

    panel.insertBefore(template.content, template);
    template.remove();
    // ランキング番号ラベルは DOMContentLoaded の1回しか当たらないので、展開を知らせる
    document.dispatchEvent(new CustomEvent('ranking:panel-hydrated', { detail: { root: panel } }));
    return true;
  }

  /** SP: スワイプで到達する前に隣のパネルを展開しておく */
  #setupLazyObserver() {
    this.#lazyObserver?.disconnect();
    this.#lazyObserver = null;

    const container = this.#panelsContainer;
    if (!container) return;

    const lazyPanels = Array.from(container.querySelectorAll('.collection-tabs__panel[data-lazy-panel="1"]'));
    if (!lazyPanels.length) return;

    this.#lazyObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const panel = /** @type {HTMLElement} */ (entry.target);
          this.#lazyObserver?.unobserve(panel);
          if (this.#hydratePanel(panel)) this.#syncPanelsHeight();
        }
      },
      // 1画面ぶん手前で展開する（スワイプ中に空白を見せない）
      { root: container, rootMargin: '0px 100% 0px 100%', threshold: 0 }
    );

    for (const panel of lazyPanels) this.#lazyObserver.observe(panel);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#panelObserver?.disconnect();
    this.#panelObserver = null;
    this.#lazyObserver?.disconnect();
    this.#lazyObserver = null;
    this.#loopAbort?.abort();
    this.#loopAbort = null;
    this.#heightObserver?.disconnect();
    this.#heightObserver = null;
  }

  #setupEventListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    const { tabs } = this.refs;

    this.addEventListener('keydown', (e) => this.#handleKeydown(e), opts);

    for (const [i, tab] of tabs.entries()) {
      tab.addEventListener('click', (e) => this.#handleTabClick(e, i), opts);
      tab.addEventListener('focus', () => this.#handleTabFocus(i), opts);
    }

    // SP: スワイプ同期はビューポート幅で有効/無効を切り替える
    this.#mobileQuery.addEventListener('change', () => this.#setupSwipeSync(), opts);

    // ユーザーが直接触ったらタブ操作起点スクロールのガードを解除
    this.#panelsContainer?.addEventListener(
      'touchstart',
      () => {
        this.#scrollTarget = null;
      },
      { signal: this.#abort.signal, passive: true }
    );

    // 初回のタッチ/ポインタ/スクロールでスワイプ系の本組みを発動（#swipeReady 参照）
    const activateSwipe = () => this.#activateSwipe();
    for (const type of ['touchstart', 'pointerdown', 'scroll']) {
      this.#panelsContainer?.addEventListener(type, activateSwipe, {
        signal: this.#abort.signal,
        passive: true,
      });
    }
  }

  /** 初回操作時にスワイプ系の本組み（クローン生成・端パネル展開・監視）へ切り替える */
  #activateSwipe() {
    if (this.#swipeReady) return;
    this.#swipeReady = true;
    this.#setupSwipeSync();
  }

  #handleKeydown(e) {
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    let targetIndex = this.#active;

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        targetIndex = this.#active > 0 ? this.#active - 1 : tabs.length - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        targetIndex = this.#active < tabs.length - 1 ? this.#active + 1 : 0;
        break;
      case 'Home':
        e.preventDefault();
        targetIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        targetIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    this.#activateSwipe();
    this.#switchTab(targetIndex);
    this.#scrollToPanel(targetIndex);
    tabs[targetIndex]?.focus();
  }

  #handleTabClick(e, index) {
    e.preventDefault();
    this.#activateSwipe();
    this.#switchTab(index);
    // focus 経由で既に active になっていても、SPではパネルスクロールが必要
    this.#scrollToPanel(index);
  }

  #handleTabFocus(index) {
    this.#activateSwipe();
    this.#switchTab(index);
  }

  #switchTab(index) {
    if (index === this.#active || index < 0) return;

    const { tabs } = this.refs;
    if (!tabs?.[index]) return;

    this.#active = index;
    this.#hydratePanel(index);
    this.#updateActiveTab();
    this.#syncPanelsHeight();
  }

  /**
   * SP: パネルの横スクロール位置から表示中カテゴリを検出してタブへ同期する。
   * scrollend が iOS Safari で未対応のため IntersectionObserver で判定する。
   */
  #setupSwipeSync() {
    this.#panelObserver?.disconnect();
    this.#panelObserver = null;
    this.#loopAbort?.abort();
    this.#loopAbort = null;
    this.#lazyObserver?.disconnect();
    this.#lazyObserver = null;
    if (!this.#panelsContainer) return;
    if (!this.#mobileQuery.matches) {
      // PC幅では非アクティブパネルが display:none なので、展開はタブ操作のときだけでよい
      // PC幅へ戻ったときに残留した高さ指定をクリアする
      this.#syncPanelsHeight();
      return;
    }

    const panels = Array.from(
      this.#panelsContainer.querySelectorAll('.collection-tabs__panel:not([data-loop-clone])')
    );
    if (panels.length < 2) return;

    // 初回表示ではアクティブタブだけを展開して初期DOMを軽くする。
    // 本組みは #activateSwipe（初回タッチ/スクロール/タブ操作）から再入する。
    if (!this.#swipeReady) {
      this.#syncPanelsHeight();
      return;
    }

    // ループクローンは中身ごと複製するので、複製元（先頭と最終）だけ先に展開しておく
    this.#hydratePanel(panels[0]);
    if (this.dataset.loop === 'true') this.#hydratePanel(panels[panels.length - 1]);

    const clones = this.#ensureLoopClones(panels);
    this.#setupLazyObserver();

    this.#panelObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const target = /** @type {HTMLElement} */ (entry.target);
          // クローンは対応する実タブとして扱う（見えている間の下線ズレ防止）
          let index = panels.indexOf(target);
          if (target === clones.tail) index = 0;
          if (target === clones.head) index = panels.length - 1;
          if (index < 0) continue;

          // タブ操作起点のスムーズスクロール中は、通過するパネルで同期しない
          if (this.#scrollTarget !== null) {
            if (index === this.#scrollTarget) this.#scrollTarget = null;
            continue;
          }

          this.#switchTab(index);
        }
      },
      { root: this.#panelsContainer, threshold: 0.6 }
    );

    for (const panel of panels) this.#panelObserver.observe(panel);
    if (clones.tail) this.#panelObserver.observe(clones.tail);
    if (clones.head) this.#panelObserver.observe(clones.head);
    if (clones.tail || clones.head) this.#setupLoopWarp(clones, panels);

    this.#syncPanelsHeight();
  }

  /**
   * コンテナ高さを表示中パネルの内容高さに合わせる。
   * 横並び(スワイプ)時はコンテナ高さが「最も高いパネル」で決まり、
   * 低いカテゴリの表示中に下へ大きな余白が出るため、アクティブパネルに追従させる。
   * 高いパネルの下端はコンテナの overflow-y: hidden で切れる（スワイプ通過中のみ見える）。
   */
  #syncPanelsHeight() {
    const container = this.#panelsContainer;
    if (!container) return;

    this.#heightObserver?.disconnect();
    this.#heightObserver = null;

    if (!this.#mobileQuery.matches) {
      container.style.height = '';
      return;
    }

    const panels = container.querySelectorAll('.collection-tabs__panel:not([data-loop-clone])');
    const active = /** @type {HTMLElement | undefined} */ (panels[this.#active]);
    if (!active) return;

    const apply = () => {
      if (!this.#mobileQuery.matches) return;
      const height = active.offsetHeight;
      if (height > 0) container.style.height = `${height}px`;
    };

    apply();
    // 画像の遅延読込などでパネル高さが後から変わるため追従する
    this.#heightObserver = new ResizeObserver(apply);
    this.#heightObserver.observe(active);
  }

  /**
   * ループ有効時、先頭パネルの表示専用クローンを末尾に1枚置く。
   * 最終パネルの右に先頭カテゴリが続いて見え、到達した瞬間に実先頭へワープする。
   * 中身の属性は一切除去しない:
   * - ref を消すと product-title 等のネストコンポーネントのJS整形（タイトル行制限など）が
   *   壊れてクローンだけ高さが変わる。内側の ref は最近接コンポーネントに帰属するため
   *   collection-tabs の refs を汚染しない（component.js の #isDescendant 判定）
   * - id はクローンが末尾に追加されるため getElementById は常に実パネル側が先勝ちで実害なし
   * ルートの id / aria-labelledby のみ外し、inert で操作は無効化する。
   */
  #ensureLoopClones(panels) {
    if (this.dataset.loop !== 'true' || !this.#panelsContainer) return { head: null, tail: null };

    if (!this.#loopClone?.isConnected) {
      const tail = this.#makeLoopClone(panels[0]);
      if (tail) {
        this.#panelsContainer.appendChild(tail);
        this.#loopClone = tail;
      }
    }

    if (!this.#loopHeadClone?.isConnected) {
      const head = this.#makeLoopClone(panels[panels.length - 1]);
      if (head) {
        this.#panelsContainer.prepend(head);
        this.#loopHeadClone = head;
        // 先頭にクローンを挟むと初期表示がクローンになるため、実先頭へ瞬間移動しておく
        this.#jumpTo(panels[0].offsetLeft);
      }
    }

    return { head: this.#loopHeadClone, tail: this.#loopClone };
  }

  /** ループ用の表示専用クローンを作る（中身の属性は保持: JS整形のrefやID scoped CSSを壊さないため） */
  #makeLoopClone(source) {
    if (!source) return null;
    // 遅延パネル(<template>)のままクローンすると中身が空になるため先に展開する
    this.#hydratePanel(source);
    const clone = /** @type {HTMLElement} */ (source.cloneNode(true));
    clone.setAttribute('data-loop-clone', '');
    clone.removeAttribute('id');
    clone.removeAttribute('aria-labelledby');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.classList.remove('collection-tabs__panel--active');
    clone.classList.add('collection-tabs__panel--hidden');
    return clone;
  }

  /** スナップを一時無効化して瞬間スクロール（ワープ・初期位置合わせ用） */
  #jumpTo(left) {
    const container = this.#panelsContainer;
    if (!container) return;
    const prevSnap = container.style.scrollSnapType;
    container.style.scrollSnapType = 'none';
    container.scrollLeft = left;
    requestAnimationFrame(() => {
      container.style.scrollSnapType = prevSnap;
    });
  }

  /** クローン到達時に対応する実パネルへワープ（スナップ再計算の震えは一時無効化で回避） */
  #setupLoopWarp(clones, panels) {
    const container = this.#panelsContainer;
    if (!container) return;

    const firstReal = panels[0];
    const lastReal = panels[panels.length - 1];

    this.#loopAbort = new AbortController();
    container.addEventListener(
      'scroll',
      () => {
        // 末尾クローン（その他の右）に到達 → 実先頭(ALL)へ
        if (clones.tail) {
          const start = clones.tail.offsetLeft;
          if (start > 0 && container.scrollLeft >= start - 1) {
            this.#jumpTo(firstReal.offsetLeft + (container.scrollLeft - start));
            this.#scrollTarget = null;
            this.#switchTab(0);
            return;
          }
        }
        // 先頭クローン（ALLの左）に到達 → 実最終(その他)へ
        if (clones.head && container.scrollLeft <= 1) {
          this.#jumpTo(lastReal.offsetLeft + container.scrollLeft);
          this.#scrollTarget = null;
          this.#switchTab(panels.length - 1);
        }
      },
      { passive: true, signal: this.#loopAbort.signal }
    );
  }

  /** SP: タブ操作時に対応パネルへスクロール（PCは従来どおり表示切替のみ） */
  #scrollToPanel(index) {
    if (!this.#panelsContainer || !this.#mobileQuery.matches) return;
    const panel = /** @type {HTMLElement | undefined} */ (
      this.#panelsContainer.querySelectorAll('.collection-tabs__panel:not([data-loop-clone])')[index]
    );
    if (!panel) return;

    this.#scrollTarget = index;
    this.#panelsContainer.scrollTo({ left: panel.offsetLeft, behavior: 'smooth' });
  }

  #updateActiveTab() {
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    const panels = this.querySelectorAll('.collection-tabs__panel:not([data-loop-clone])');
    if (!panels.length) return;

    // View All リンクをアクティブタブのコレクションURLに更新
    const viewAllLink = this.querySelector('[data-ranking-view-all]');
    const activeTab = tabs[this.#active];
    if (viewAllLink && activeTab) {
      const handle = activeTab.getAttribute('data-collection-handle');
      if (handle) {
        viewAllLink.setAttribute('href', `/collections/${handle}`);
      }
    }

    // スワイプ同期でタブが切り替わったとき、アクティブタブが見える位置までタブバーを送る
    const tablist = this.querySelector('.collection-tabs__tablist');
    if (tablist && activeTab && tablist.scrollWidth > tablist.clientWidth) {
      const delta = activeTab.getBoundingClientRect().left - tablist.getBoundingClientRect().left;
      tablist.scrollTo({
        left: tablist.scrollLeft + delta - (tablist.clientWidth - activeTab.offsetWidth) / 2,
        behavior: 'smooth',
      });
    }

    for (const [i, tab] of tabs.entries()) {
      const isActive = i === this.#active;
      const panel = panels[i];

      // タブの状態を更新
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      tab.classList.toggle('collection-tabs__tab--active', isActive);

      // パネルの状態を更新
      if (panel) {
        if (isActive) {
          panel.classList.remove('collection-tabs__panel--hidden');
          panel.classList.add('collection-tabs__panel--active');
          panel.removeAttribute('inert');

          // lazy画像を強制ロード + content-visibility解除
          // SPでは隣接パネルも先読みしてスワイプ中の空白を防ぐ
          requestAnimationFrame(() => {
            const preloadPanels = this.#mobileQuery.matches
              ? [panel, panels[i - 1], panels[i + 1]]
              : [panel];
            for (const p of preloadPanels) {
              if (!p) continue;
              p.querySelectorAll('img[loading="lazy"]').forEach((img) => {
                img.loading = 'eager';
              });
            }
            panel.querySelectorAll('slideshow-slide').forEach((slide) => {
              slide.style.contentVisibility = 'visible';
            });
          });
        } else {
          panel.classList.add('collection-tabs__panel--hidden');
          panel.classList.remove('collection-tabs__panel--active');
          panel.setAttribute('inert', '');
        }
      }
    }
  }
}

customElements.define('collection-tabs-component', CollectionTabsComponent);
